import { styled } from '@mui/material/styles';

export const StyledRoot = styled('div', {
  shouldForwardProp: (prop) => prop !== 'fillRemaining',
})(({ theme, fillRemaining }) => ({
  // Default: flow under NavBarContent; CourseDocument is the scrollport.
  // fillRemaining: interactive docs — fill under header; iframe scrolls inside.
  flex: fillRemaining ? '1 1 auto' : '0 0 auto',
  minHeight: fillRemaining ? 0 : undefined,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: fillRemaining ? 'hidden' : 'visible',

  '& .MuiTab-labelIcon': {
    minHeight: 50,
  },

  '@media print': {
    position: 'relative',
    overflow: 'visible',
    height: 'auto',
  },

  '& .content': {
    padding: theme.spacing(2),

    '& .buttonRow button': {
      margin: theme.spacing(1),
    },
  },
}));

export const StyledQuestionsContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ theme, isMobile }) => ({
  flex: '0 0 auto',
  width: '100%',
  maxWidth: '1040px',
  margin: '0 auto',
  position: 'relative',
  alignSelf: 'stretch',
  overflow: 'visible',
  // Tall enough that Prev/Next sit in the first screenful; CourseDocument still
  // scrolls the course header away with the rest of the page.
  minHeight: 'calc(100vh - 180px)',
  display: 'flex',
  flexDirection: 'column',

  '& .scrollContainer': {
    position: 'relative',
    flex: '1 1 auto',
    overflow: 'visible',
    margin: `${theme.spacing(1)} auto`,
    padding: theme.spacing(1),
    width: '100%',
    boxSizing: 'border-box',
  },

  '&.resultsContainer': {
    minHeight: 'auto',
    // Center when the panel is wide; keep spacing(2) side gaps when the
    // sidebar leaves a narrow content column (margin:auto alone collapses to 0).
    width: `min(1040px, calc(100% - ${theme.spacing(4)}))`,
    maxWidth: '1040px',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginLeft: 'auto',
    marginRight: 'auto',
    boxSizing: 'border-box',

    '& .questionContainer': {
      width: isMobile ? '100%' : '40%',
    },

    '& .answerContainer': {
      width: isMobile ? '80%' : '50%',
      maxWidth: 500,
      margin: theme.spacing(2),
      padding: theme.spacing(1),
      backgroundColor: theme.palette.action.disabledBackground,
      borderRadius: theme.spacing(1),
    },

    '& .question': {
      flex: 1,
      maxWidth: 500,
      margin: `${theme.spacing(5)} 0`,
      minHeight: 'auto',
    },

    '& .showAnswer': {
      '& .checkpointOption': {
        backgroundColor: theme.palette.action.disabledBackground,
        margin: `${theme.spacing(1)} ${theme.spacing(1)} 0 0`,
        padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,

        '&.incorrect': {
          '&.selected': {
            backgroundColor: theme.palette.error.main,
          },

          '& .MuiIcon-root': {
            color: theme.palette.error.main,
          },
        },
        '&.correct': {
          '&.selected': {
            backgroundColor: theme.palette.success.main,
          },

          '& .MuiIcon-root': {
            color: theme.palette.success.main,
          },
        },

        '& .MuiIcon-root': {
          padding: theme.spacing(1),
        },

        '&.selected.incorrect .MuiIcon-root, &.selected.correct .MuiIcon-root': {
          color: theme.palette.common.white,
        },
      },
    },
  },

  '& .buttonContainer': {
    // Stick within CourseDocument scrollport (not the viewport chrome).
    position: 'sticky',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1100,
    flexShrink: 0,
    backgroundColor: theme.palette.background.paper,
    paddingBottom: theme.spacing(1),
  },

  '& .buttonRow': {
    position: 'relative',
    bottom: 'auto',
    justifyContent: 'center',

    '& button': {
      margin: theme.spacing(1),
    },
  },

  '& .prog': {
    width: isMobile ? '95%' : '100%',
    maxWidth: 500,
    position: 'relative',
    bottom: 'auto',
    margin: '0 auto',
  },

  '& .question': {
    minHeight: 500,
    maxWidth: 500,
    margin: '0 auto',
    paddingBottom: 70,

    '&.intro': {
      textAlign: 'center',

      '& .header': {
        paddingTop: theme.spacing(3),
      },
    },

    '& .submitRow': {
      marginTop: theme.spacing(2),
    },

    '& .header': {
      marginBottom: theme.spacing(2),
    },
    '& .options': {
      margin: theme.spacing(2),

      '&.boolean': {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',

        '& .checkpointOption': {
          paddingRight: theme.spacing(2),
        },
      },
    },
    '& .checkpointOption': {
      borderRadius: theme.spacing(1),
      '&:hover': {
        backgroundColor: isMobile ? undefined : theme.palette.action.disabledBackground,
        cursor: 'pointer',
      },
    },

    '&.checkpoint': {
      paddingTop: theme.spacing(2),
      alignItems: 'flex-start',

      '&.withImages': {
        maxWidth: 800,
      },

      '& .textContainer': {
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        minHeight: 800,
        width: '50%',
      },

      '& .why': {
        marginTop: theme.spacing(5),
      },

      '& .imageContainer': {
        width: '50%',
        marginLeft: theme.spacing(1),
      },
    },
  },
}));
